/**
 * S5.6a — die Korrektur von Hand. Der EINE Dialog für beide Einstiege
 * (`RefundEntscheidungen.jsx` und `InvoiceDetail.jsx`).
 *
 *   GET  /api/invoicing/admin/korrektur/kontext?refund_id=   → die Vorlage
 *   POST /api/invoicing/admin/korrektur/vorschau             → Entwurfs-PDF
 *   POST /api/invoicing/admin/korrektur/ausstellen           → das Dokument
 *
 * 🔴 **Der Server liefert die Lage, nicht der Client.** Datum, Betrag und das
 *    Original kommen aus `handweg_lage`; das `original_id` in jeder Anfrage ist
 *    nur die **Bestätigung** dessen, was hier steht (E8) — weicht es ab, antwortet
 *    der Server 409 `original_weicht_ab`.
 *
 * 🔴 **Die Zeilenform ist geschlossen (E9).** Geschickt wird **genau**
 *    `{shopify_line_item_id, menge}` — kein `brutto`, kein zweiter Bezug. Die
 *    Vorschau und das Ausstellen bauen die Zeilen über DIESELBE Funktion.
 *
 * 🔴 **Der Grund ist eine geschlossene Codemenge** (Bedienertext, kein
 *    Kundentext). `teilerstattung` und `kulanz` gibt es hier NICHT — sie kommen
 *    mit S5.6b (P5-O9).
 *
 * 🔴 **Die Vorschau ist kein Dokument.** Sie trägt die Entwurfsnummer
 *    `ENTWURF-000000` und wird als solche gerahmt. Sie ist **nicht** an
 *    Korrekturmodus und Text-Riegel gebunden (sie erzeugt nichts); das Ausstellen
 *    ist es — `uebersprungen` heisst „nicht ausgestellt, der Modus ist der Grund"
 *    (ohne Moduswert: `off` | `pilot` | `live`), `gesperrt` mit
 *    `korrekturtext_nicht_freigegeben` heisst „Texte nicht freigegeben (P4-O1)".
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { api } from '../../api/client'
import PdfInlineFrame from '../../components/common/PdfInlineFrame'
import { FS, PANEL_RADIUS, SP } from '../../theme/tokens'
import DisabledReason from '../../components/common/DisabledReason';

const STRICH = '—'
const BASIS = '/api/invoicing/admin/korrektur'

// ── Die geschlossene Codemenge des Grundes ─────────────────────────────────
// Deckungsgleich mit `korrektur.GRUND_CODES` (Backend). Kein `teilerstattung`,
// kein `kulanz` — die kommen mit S5.6b.
export const GRUND_LABEL = {
  ruecksendung: 'Return',
  versand_erstattet: 'Shipping refunded',
  sonstiges: 'Other',
}
export const GRUND_REIHENFOLGE = ['ruecksendung', 'versand_erstattet', 'sonstiges']

/** Die Arten, die in die Handkorrektur führen (Backend: `ZULAESSIGE_ARTEN`). */
export const ZULAESSIGE_ARTEN = ['korrektur_aus', 'mensch', 'korrektur_betrag']

// ── Die deutschen Sätze zu den Antwortzuständen ────────────────────────────
export const AUSGANG_TEXT = {
  ausgestellt: 'The correction has been issued.',
  // Gilt in beiden Lagen von `schon_erledigt` (`korrektur._schon_vorhanden`): die
  // Korrekturzeile steht ODER die Entscheidung wurde ohne Dokument erledigt
  // (N6). Der Link erscheint nur, wenn es ein Dokument gibt.
  schon_erledigt: 'The decision for this refund is already resolved — nothing was changed.',
  // 🔴 Kein Moduswert im Satz. `modus()` liefert `off` | `pilot` | `live`, und
  // `uebersprungen` tritt AUCH im Pilot auf (Bestellung nicht in der Pilotliste).
  // Den echten Wert nennt die Grundzeile darunter (`REST_GRUND_TEXT.modus`).
  uebersprungen: 'No correction was issued — the correction mode does not allow it.',
  gesperrt: 'The correction was not issued.',
}

/**
 * Der Hinweis zum Korrekturmodus IM Formular — **je Modus**, und ohne eine
 * Aussage über DIESE Bestellung (R16 O-3).
 *
 * 🔴 Der alte Satz war im `pilot` schlicht FALSCH: er behauptete für JEDE
 * Bestellung „es wird nichts ausgestellt". `darf_korrigieren`
 * (`korrektur.py:303`) gibt aber eine gelistete Bestellung frei — dort WIRD
 * ausgestellt. Das Frontend kennt `modus` (`off` | `pilot` | `live`), aber
 * NICHT die Pilotliste (`INVOICING_PILOT_ORDERS`) — der Satz darf deshalb
 * nichts über den Ausgang dieser Bestellung behaupten, nur über den Modus.
 *
 * `off`   — `darf_korrigieren` gibt nichts frei; „es wird keine Korrektur
 *           ausgestellt" ist hier unbedingt wahr.
 * `pilot` — freigegeben ist nur, was in der Pilotliste steht. Der Satz nennt die
 *           Bedingung, nicht das Ergebnis: eine gelistete Bestellung wird sehr
 *           wohl ausgestellt.
 * `live`  — kein Hinweis (der Zweig rendert nichts).
 */
export const MODUS_HINWEIS = {
  off: 'The correction mode is set to “off” — no correction is issued.',
  pilot: 'The correction mode is set to “pilot” — corrections are issued only for orders on the pilot list.',
}

/**
 * Die Kerngründe als Satz — je Zeile (`zeile_<n>_<code>`) ODER zeilenlos.
 *
 * 🔴 **Deckungsgleich mit den `_Ablehnung`-Codes des Kerns** (`korrektur.py`).
 * Der Kern hängt an den Code einen Doppelpunkt mit Werten (`ueberkorrektur_menge:4>3`);
 * `gruendeJeZeile` schneidet ihn ab. Die Mengen-Probe in
 * `__tests__/KorrekturDialog.test.jsx` hält die Schlüssel gegen die Quelle —
 * ein fehlender Code fiele sonst roh in die Oberfläche (R16-W3).
 */
export const ZEILENGRUND_TEXT = {
  ueberkorrektur_menge: 'Quantity above the remainder',
  ueberkorrektur_brutto: 'Amount above the remainder',
  ueberkorrektur_steuer: 'Tax above the remainder',
  ohne_id: 'This item has no Shopify line and cannot be corrected',
  ohne_bezug: 'No reference given',
  unbekannte_id: 'Unknown item',
  unbekannte_position: 'Unknown item',
  ohne_werte: 'Quantity missing',
  negativ: 'Quantity or amount is negative',
  menge_null: 'Quantity is zero',
  original_menge_null: 'The original line has no quantity',
  brutto_unter_rest: 'Amount below the remainder',
  betrag_ohne_menge_handfall: 'An amount without a quantity is not possible',
  brutto_und_erstattet: 'A line carries amount AND quantity — only the quantity is allowed here',
  anteilig_mischsatz: 'The invoice carries several tax rates — this case needs a decision',
  ueberkorrektur_erstattet: 'The correction exceeds the refunded amount',
  // ── Versandzeilen im Korrektur-Kern (A1, 2026-10-31; #4150) ──────────────
  // 🔴 Diese zwoelf Codes fielen bis zum 2026-11-01 roh in den Dialog — genau
  // beim Alltagsfall „Versand mit erstatten". Je Code: was ihn ausloest und was
  // der Bediener tun kann (`backend/invoicing/korrektur.py`).
  versand_ohne_id:
    'A refunded shipping line has no Shopify id — correct this by hand',
  versand_unbekannte_id:
    'The refunded line is not a shipping line on this invoice — check the refund',
  versand_mehrere_zeilen:
    'More than one shipping line matches — the match is ambiguous, correct by hand',
  versand_original_null:
    'The shipping was free (0.00) and cannot be reduced',
  versand_betrag_null:
    'No amount left on this shipping line — it is already fully corrected, or enter a positive amount',
  versand_ueber_rest:
    'Shipping amount is above the refundable remainder — reduce the amount',
  versand_steuer_ueber_rest:
    'Shipping tax is above the refundable remainder — reduce the amount',
  versand_ohne_erstattet:
    'The refunded amount is missing — reopen the refund',
  versand_doppelt:
    'The shipping line is listed twice — reload the refund',
  versand_ueber_erstattet:
    'Shipping exceeds the refunded amount',
  versand_und_freier_betrag:
    'No items, and the amount exceeds the shipping sum — check the refund',
  versand_und_positionen_ohne_betrag:
    'All of the refund is shipping — no item amount remains',
  original_ohne_positionen: 'The original invoice has no items',
  original_zeile_ohne_id: 'An original line has no Shopify line',
  rundung_negativ: 'The correction would round negative',
  oss_ohne_bestimmungsland: 'The OSS case is missing the destination country',
  altbeleg_betrag_null: 'The old receipt carries no amount',
  altbeleg_kein_einzelsatz: 'The old receipt is not a single line',
  altbeleg_steuerfall_fehlt: 'The old receipt is missing the tax case',
  vorzeichen_total_gross: 'The correction carries the wrong sign',
  vorzeichen_zeile: 'A correction line carries the wrong sign',
  // Kein `_Ablehnung`-Code: der Rückfall des Vorflugs für jeden anderen Fehler
  // (`vorflug:<Typ>: <Meldung>`, `korrektur.py:1373`).
  vorflug: 'The correction could not be rendered',
}

/**
 * Die **zeilenlosen** Gründe, die der ERFOLGSPFAD wirklich erreichen kann —
 * als Satz, mit dem Wert aus dem Doppelpunkt-Anhang (R15 W-1).
 *
 * 🔴 **Aus der Quelle abgeleitet, nicht geraten.** Der Handweg
 * (`korrektur_routes.py` → `korrektur.korrektur_ausstellen`) antwortet mit
 * HTTP 200 für **jedes** `Ergebnis`; `gruende=` steht dort an genau diesen
 * Stellen (`grep -n "gruende=" backend/invoicing/korrektur.py`):
 *
 *   `_schon_vorhanden`     `status:<status>` · `entscheidung:erledigt`
 *   `korrektur_ausstellen` `original_fehlt` · `modus:<modus()>` ·
 *                          `korrekturtext_nicht_freigegeben[:…]`
 *   `_handfall`            `vorflug:<Typ>: …` · `ausstellung:<Typ>: …` ·
 *                          `entscheidung:<fehler>` · die `_Ablehnung`-Codes
 *   `_transaktion`         `original_fehlt` · `original_status:<status>` ·
 *                          `status:<status>` · die `_Ablehnung`-Codes
 *   `_dez` (jeder Weg)     `<name>_unlesbar:<fehler>` — die einzige DYNAMISCHE
 *                          Familie (unten über das Suffix gefangen)
 *
 * Der Wert steht jeweils **hinter** dem Doppelpunkt; `gruendeJeZeile` schneidet
 * ihn ab und reicht ihn hier als `wert` durch. Ein Code ohne Eintrag fällt
 * weiterhin **roh** durch (letzter Zweig) — Unbekanntes verschwindet nicht
 * still, aber die erreichbaren Familien treffen ihn nicht mehr.
 */
export const REST_GRUND_TEXT = {
  // `modus()` liefert `off` | `pilot` | `live` (`korrektur.py:287`). Eigener
  // Satz MIT Wert: die Ausgangszeile (`AUSGANG_TEXT.uebersprungen`) behauptet
  // keinen Moduswert mehr, weil `pilot` (Bestellung nicht in der Pilotliste,
  // `darf_korrigieren`) erreichbar ist — dort wäre ein fester Wert schlicht
  // falsch. Der Wert gehört sichtbar in den Grund.
  modus: (wert) => `The correction mode “${wert}” does not allow issuance.`,
  status: (wert) =>
    `A correction for this refund is already in state “${wert}”.`,
  original_status: (wert) =>
    `The original invoice is in state “${wert}” — no correction is made from it.`,
  // Zwei Lagen, ein Code: `entscheidung:erledigt` ist die erledigte Entscheidung
  // (`_schon_vorhanden`), `entscheidung:<fehler>` die NICHT festgehaltene
  // (`_handfall`) — die zweite ist die wichtigere: der Fall ist nicht protokolliert.
  entscheidung: (wert) => (wert === 'erledigt'
    ? 'This decision is already set to “resolved”.'
    : 'The decision could not be recorded — the case is not logged.'),
  original_fehlt: () => 'The original invoice no longer exists.',
  // Über den Handweg heute nicht erreichbar (nur `korrektur_altbeleg_ausstellen`),
  // aber der Kern setzt ihn (`korrektur.py:1984/2125`) und der Dialog ist geteilt.
  altbeleg_fehlt: () => 'The old receipt no longer exists.',
  // Der Ausnahmefall der Ausstellung (`korrektur.py:1708`). Nur der Typname, nie
  // die Meldung — eine Python-Meldung ist kein Bedienertext.
  ausstellung: (wert) =>
    `Issuance failed unexpectedly (${String(wert ?? '').split(':')[0] || 'unknown'}) — the case is recorded as a manual case.`,
}

/** Die Fehlercodes der drei Routen als Satz — **kein roher Code** (R16-W1). */
export const FEHLER_TEXT = {
  kein_schreibkonto:
    'This account may not issue a correction. Sign in with the write account and try again.',
  grund_unbekannt:
    'The selected reason is unknown. Pick a reason from the options.',
  zeilenform:
    'A line has an unexpected field. Reload the template and try again.',
  original_weicht_ab:
    'The original invoice has changed. Close the dialog and reopen the correction.',
  original_unbekannt:
    'No original invoice is stored for this decision — a correction is not possible here.',
  original_ohne_positionen:
    'The original invoice has no items — a correction is not possible here.',
  ausfuhrvorgang:
    'This invoice has a self-export case, so it cannot be corrected here. Handle this refund manually with accounting, then close the decision in Refund decisions.',
  steuerplausibilitaet_offen:
    'The tax of the refund cannot be checked against the invoice. This case needs a decision (P5-O9).',
  betrag_weicht_von_erstattung_ab:
    'The correction amount differs from the refunded amount. Only the actually refunded amount can be corrected.',
  korrektur_abgelehnt:
    'The correction was rejected. The reasons are below.',
  entscheidung_unbekannt:
    'There is no open decision for this refund. Reload the list.',
  zustand_nicht_offen:
    'This decision is no longer open. Reload the list.',
  art_nicht_zulaessig:
    'This decision does not lead into the correction. It is closed or handled otherwise.',
  refund_datum_fehlt:
    'The refund is missing the date. Without a date there is no correction (the tax period depends on it).',
  nicht_endgueltig:
    'The refund is not yet final (open or unconfirmed). Wait until it is completed.',
  refund_unbekannt:
    'The refund can no longer be read from Shopify. Reload and try again.',
  refund_unlesbar:
    'The refund cannot be read from Shopify completely. Reload and try again.',
  refund_id_fehlt: 'The request names no refund.',
  refund_id_ungueltig: 'The refund identifier is invalid.',
  original_id_ungueltig: 'The confirmation of the original invoice is invalid.',
}

const FEHLER_RUECKFALL =
  'The request failed. Reload the template and try again.'

/**
 * Die `kontext`-Codes, bei denen ein neuer Versuch SICHER dieselbe Antwort gibt
 * (R16 W1, Handweg-Sperre 2026-10-27): dort kein „Try again", und die deaktivierten
 * Knöpfe nennen „Not available for this invoice." statt eines Ladefehlers.
 * Bewusst eng — nur, was die Sperre neu bringt; ältere Dauer-Codes behalten ihr
 * Verhalten.
 */
export const DAUERHAFTE_LADECODES = new Set(['ausfuhrvorgang'])

export const fehlertext = (daten) =>
  (daten && (FEHLER_TEXT[daten.error] || daten.error)) || FEHLER_RUECKFALL

/**
 * `zeile_<n>_<code>[:werte]` → `{n: satz}`; alles andere wird ein Satz im `rest`.
 *
 * 🔴 **Der Doppelpunkt-Anhang gehört nicht zum Code.** Der Kern schreibt
 * `zeile_1_ueberkorrektur_menge:4>3` und `ueberkorrektur_erstattet:4>3`; ohne
 * das Abschneiden bliebe der ganze String roh stehen (R16-W3).
 *
 * Die zeilenlosen Gründe gehen durch drei Stufen: `ZEILENGRUND_TEXT` (die
 * `_Ablehnung`-Codes), `REST_GRUND_TEXT` (die Familien des Erfolgspfads, mit
 * Wert) und die dynamische `*_unlesbar`-Familie. Was keine davon kennt, bleibt
 * **roh** — Unbekanntes darf nicht stillschweigend verschwinden (R15 W-1).
 */
export function gruendeJeZeile(gruende) {
  const jeZeile = {}
  const rest = []
  for (const g of gruende || []) {
    const roh = String(g)
    const code = roh.split(':')[0]
    const wert = roh.slice(code.length + 1) || undefined
    const treffer = /^zeile_([0-9]+)_(.+)$/.exec(roh)
    if (treffer) {
      const satz = ZEILENGRUND_TEXT[treffer[2].split(':')[0]] || roh
      jeZeile[Number(treffer[1])] = (jeZeile[Number(treffer[1])] || []).concat(satz)
    } else if (code === 'korrekturtext_nicht_freigegeben') {
      rest.push('Texts not released (P4-O1) — nothing was issued.')
    } else if (ZEILENGRUND_TEXT[code]) {
      rest.push(ZEILENGRUND_TEXT[code])
    } else if (REST_GRUND_TEXT[code]) {
      rest.push(REST_GRUND_TEXT[code](wert))
    } else if (code.endsWith('_unlesbar')) {
      // Die einzige DYNAMISCHE Familie (`korrektur._dez`): `<feld>_unlesbar:<fehler>`.
      // Der Feldname steht vor dem Suffix, der Fehlertext ist kein Bedienertext.
      rest.push(`A value of the invoice could not be read (${code.slice(0, -'_unlesbar'.length)}).`)
    } else {
      rest.push(roh)
    }
  }
  return { jeZeile, rest }
}

/** Eine Antwort, die als Blob kam (axios `responseType: 'blob'`), auspacken. */
async function alsJson(daten) {
  if (daten && typeof daten.text === 'function' && typeof daten.type === 'string') {
    try { return JSON.parse(await daten.text()) } catch { return null }
  }
  return daten
}

/** Die Zeilen aus dem Formular — **genau** `{shopify_line_item_id | position, menge}` (E9). */
export function baueZeilen(vorlage, mengen) {
  return vorlage.map((z, i) => {
    const wert = mengen[i]
    const zeile = z.shopify_line_item_id
      ? { shopify_line_item_id: z.shopify_line_item_id }
      : { position: z.position }
    return { ...zeile, menge: String(wert).trim() }
  }).filter((z) => z.menge !== '' && Number(z.menge) > 0)
}

const zahl = (w) => (w == null || w === '' ? STRICH : String(w))

/**
 * @param offen         Dialog offen?
 * @param refundId      der Refund der Entscheidung
 * @param onSchliessen  Dialog schliessen
 * @param onErgebnis    (text, ausgang) — die Seite lädt neu und hält den Satz;
 *                      `ausgang` ist die volle Antwort (`zustand`, `invoice_id`,
 *                      `nummer`, `gruende`) und färbt die Listenmeldung (R16-B2)
 */
export default function KorrekturDialog({ offen, refundId, onSchliessen, onErgebnis }) {
  const theme = useTheme()
  const schmal = useMediaQuery(theme.breakpoints.down('sm'))
  const titelId = useId()

  const [vorlage, setVorlage] = useState(null)
  const [laedt, setLaedt] = useState(false)
  const [ladefehler, setLadefehler] = useState(null)
  const [ladeCode, setLadeCode] = useState(null)
  const [mengen, setMengen] = useState({})
  const [grund, setGrund] = useState('')
  const [sendet, setSendet] = useState(false)
  const [fehler, setFehler] = useState(null)
  const [gruende, setGruende] = useState([])
  const [vorschau, setVorschau] = useState(null)
  const [ergebnis, setErgebnis] = useState(null)

  // 🔴 Die Objekt-URL des Entwurfs wird **ausdrücklich** freigegeben, nicht über
  // einen Aufräum-Effekt: unter `StrictMode` liefe dessen Cleanup direkt nach dem
  // Setzen und zöge die URL unter dem angezeigten Rahmen weg.
  const vorschauUrl = useRef(null)
  const setzeVorschau = useCallback((neu) => {
    if (vorschauUrl.current && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(vorschauUrl.current)
    }
    vorschauUrl.current = neu?.url || null
    setVorschau(neu)
  }, [])

  const laden = useCallback(() => {
    if (!refundId) return undefined
    let abgemeldet = false
    setLaedt(true)
    setLadefehler(null)
    setLadeCode(null)
    api.get(`${BASIS}/kontext`, { params: { refund_id: refundId } })
      .then(({ data }) => {
        if (abgemeldet) return
        setVorlage(data)
        setMengen(Object.fromEntries(
          (data.zeilen || []).map((z, i) => [i, ''])))
        setLaedt(false)
      })
      .catch(async (e) => {
        if (abgemeldet) return
        setLaedt(false)
        const daten = await alsJson(e?.response?.data)
        setLadeCode((daten && daten.error) || null)
        setLadefehler(fehlertext(daten) || e?.message)
      })
    return () => { abgemeldet = true }
  }, [refundId])

  useEffect(() => {
    if (!offen) {
      setVorlage(null); setMengen({}); setGrund(''); setFehler(null)
      setGruende([]); setzeVorschau(null); setErgebnis(null)
      return
    }
    return laden()
  }, [offen, laden, setzeVorschau])

  // Die Mengen des Refunds sind die Vorbelegung (Backend: `refund_mengen`).
  useEffect(() => {
    if (!vorlage) return
    const vorbelegt = {}
    ;(vorlage.zeilen || []).forEach((z, i) => {
      const treffer = (vorlage.refund_mengen || [])
        .find((m) => m.shopify_line_item_id && m.shopify_line_item_id === z.shopify_line_item_id)
      if (treffer) vorbelegt[i] = String(treffer.menge)
    })
    setMengen((alt) => ({ ...alt, ...vorbelegt }))
  }, [vorlage])

  // 🔴 Eigenes `useMemo`: `vorlage?.zeilen || []` erzeugte bei fehlender Liste
  // in JEDEM Render ein neues Array — die zwei `useMemo` darunter sähen dann
  // eine wechselnde Abhängigkeit (react-hooks/exhaustive-deps, R9).
  const zeilen = useMemo(() => vorlage?.zeilen || [], [vorlage])
  const zeilenfehler = useMemo(() => {
    const fehlerMap = {}
    zeilen.forEach((z, i) => {
      const wert = mengen[i]
      if (wert === '' || wert == null) return
      const menge = Number(wert)
      if (!Number.isFinite(menge) || menge <= 0) {
        fehlerMap[i] = 'Quantity must be greater than 0.'
      } else if (z.menge_rest != null && menge > Number(z.menge_rest)) {
        fehlerMap[i] = `At most ${z.menge_rest} (remainder).`
      }
    })
    return fehlerMap
  }, [zeilen, mengen])

  // 🔴 Die Nummerierung des Kerns (`zeile_<n>_…`) zählt die **geschickten**
  // Zeilen, nicht die Formularzeilen. Diese Liste bildet sie zurück — sonst
  // stünde der Grund an der falschen Position.
  const aktiveIndizes = useMemo(
    () => zeilen.map((_, i) => i).filter((i) => mengen[i] !== '' && mengen[i] != null
      && Number(mengen[i]) > 0),
    [zeilen, mengen])

  const gewaehlt = aktiveIndizes.length
  // 🔴 Zwei Bedingungen, nicht eine (R16-W5). Die **Vorschau** braucht keinen
  // Grund — ihr Körper trägt keinen (`koerper(false)`), und der Entwurf ist
  // gerade dafür da, vor der Entscheidung zu wirken. Das **Ausstellen** braucht
  // ihn, und der Text-Riegel (P4-O1) sperrt nur das Ausstellen: der Info-Alert
  // sagt „ausstellen ist gesperrt", also ist der Knopf auch gesperrt.
  const basisUnvollstaendig = gewaehlt === 0
    || Object.keys(zeilenfehler).length > 0 || !vorlage?.steuer_plausibel
  const ausstellenGesperrt = basisUnvollstaendig || !grund || !vorlage?.texte_freigegeben
  // 🔴 R16 W2 (Handweg-Sperre, 2026-10-27): ohne Vorlage sind beide Knöpfe aus,
  // weil das LADEN scheiterte — „Select at least one line first." wäre dort falsch.
  const ladeDauerhaft = DAUERHAFTE_LADECODES.has(ladeCode)
  const ladegrund = !ladefehler ? null
    : ladeDauerhaft ? 'Not available for this invoice.'
      : 'The template could not be loaded.'

  const koerper = (mitGrund) => {
    const daten = {
      refund_id: refundId,
      original_id: vorlage?.original?.invoice_id,
      zeilen: baueZeilen(zeilen, mengen),
    }
    if (mitGrund) daten.grund = grund
    return daten
  }

  const zeigeFehler = async (e) => {
    const daten = await alsJson(e?.response?.data)
    setFehler(fehlertext(daten))
    setGruende((daten && daten.gruende) || [])
  }

  const holeVorschau = () => {
    if (basisUnvollstaendig || sendet) return
    setSendet(true); setFehler(null); setGruende([]); setzeVorschau(null)
    api.post(`${BASIS}/vorschau`, koerper(false), { responseType: 'blob' })
      .then((antwort) => {
        const blob = antwort.data instanceof Blob
          ? antwort.data : new Blob([antwort.data], { type: 'application/pdf' })
        setzeVorschau({
          url: URL.createObjectURL(blob),
          korrekturbetrag: antwort.headers?.['x-korrekturbetrag'],
          erstattet: antwort.headers?.['x-erstattet'],
        })
        setSendet(false)
      })
      .catch(async (e) => { setSendet(false); await zeigeFehler(e) })
  }

  const stelleAus = () => {
    if (ausstellenGesperrt || sendet) return
    setSendet(true); setFehler(null); setGruende([])
    api.post(`${BASIS}/ausstellen`, koerper(true))
      .then(({ data }) => {
        setSendet(false)
        setErgebnis(data)
        // 🔴 R16-B1: auch der ERFOLGSPFAD trägt Gründe — `gesperrt` liefert sie
        // mit HTTP 200 (`korrektur_routes.py`), und heute ist das der Hauptpfad
        // (Text-Riegel leer, P4-O1). Ohne diese Zuweisung blieb `restGruende`
        // leer und der Bediener sah den Grund nie.
        setGruende(data.gruende || [])
        onErgebnis?.(AUSGANG_TEXT[data.zustand] || AUSGANG_TEXT.gesperrt, data)
      })
      .catch(async (e) => { setSendet(false); await zeigeFehler(e) })
  }

  // 🔴 R16 O-1: der Grund je Zeile gehört zu dem Formular, das ihn ausgelöst hat.
  // `jeZeile` kommt aus dem SERVERzustand `gruende`, `aktiveIndizes` aus dem
  // LEBENDEN Formular — ändert der Bediener die Mengen, rutschte `zeile_1_…` auf
  // eine andere (nie beanstandete) Formularzeile und blieb dort rot. Jede
  // Änderung am Formular verwirft die Gründe deshalb.
  // 🔴 Der ERGEBNISblock ist davon NICHT betroffen: er liest den Schnappschuss
  // `ergebnis.gruende` (R16 W1) — diese Trennung darf nicht wieder einreissen.
  const formGeaendert = () => setGruende((alt) => (alt.length ? [] : alt))

  // Der lebende State trägt die FORMULARbeschriftung (je Zeile) und den
  // `fehler`-Alert eines Nicht-200. Der ERGEBNISblock liest dagegen aus dem
  // **Schnappschuss** der Antwort (`ergebnis.gruende`): `holeVorschau` leert
  // `gruende` (`setGruende([])`) und lässt `ergebnis` stehen — am State hinge
  // der Grund nach einem „Vorschau"-Klick nicht mehr in der sichtbaren Meldung
  // (R16 W1).
  const { jeZeile, rest: restGruende } = gruendeJeZeile(gruende)
  const restErgebnis = gruendeJeZeile(ergebnis?.gruende).rest
  // 🔴 R16 W2: nur ENDGÜLTIGE Ausgänge sperren „Ausstellen". `gesperrt` und
  // `uebersprungen` sind behebbar (Menge ändern, Modus, Text-Riegel) — der
  // Bediener muss den Weg, den die Zeilenbeschriftung gerade anbietet, auch
  // gehen können. Bei `ausgestellt`/`schon_erledigt` wäre die Wiederholung
  // sinnlos und ersetzte die gelesene Nummer durch die idempotente Antwort.
  const ausgangEndgueltig = ergebnis?.zustand === 'ausgestellt'
    || ergebnis?.zustand === 'schon_erledigt'
  // Die Kerngründe je Zeile an der Formularzeile, zu der sie gehören.
  const grundNachFormIndex = {}
  aktiveIndizes.forEach((formIdx, k) => {
    if (jeZeile[k + 1]) grundNachFormIndex[formIdx] = jeZeile[k + 1]
  })

  return (
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id={titelId}>Create correction</DialogTitle>
      <DialogContent dividers>
        {laedt && (
          <Stack direction="row" spacing={SP.chip} alignItems="center" aria-busy="true">
            <CircularProgress size={20} aria-hidden="true" />
            <Typography variant="body2">Loading template …</Typography>
          </Stack>
        )}

        {ladefehler && (
          <Alert
            severity="error"
            action={ladeDauerhaft
              ? null
              : <Button size="small" color="inherit" onClick={laden}>Try again</Button>}
          >
            {ladefehler}
          </Alert>
        )}

        {vorlage && (
          <Stack spacing={SP.card}>
            <Typography variant="body2">
              Original invoice <strong>{vorlage.original?.nummer || vorlage.original?.invoice_id}</strong>
              {' '}({vorlage.original?.datum || STRICH}) · Refund {refundId}
            </Typography>
            {/* R16-W1: die Folge des Ausstellens steht IM Dialog (Muster
                `InvoiceDetail.jsx`, IgL-Freigabe) — nicht erst nach dem Klick. */}
            <Typography variant="body2" data-testid="unwiderruflich" sx={{ color: 'text.secondary' }}>
              “Issue” consumes a GS number and cannot be undone — the correction is logged
              indelibly under your account.
            </Typography>
            {!vorlage.steuer_plausibel && (
              <Alert severity="warning">
                {FEHLER_TEXT.steuerplausibilitaet_offen}
              </Alert>
            )}
            {!vorlage.texte_freigegeben && (
              <Alert severity="info">
                The correction texts are not released yet (P4-O1) — issuing is blocked,
                the preview works.
              </Alert>
            )}
            {vorlage.modus !== 'live' && (
              // 🔴 R16 O-3: der Satz gilt für JEDEN Modus. Im `pilot` wird eine
              // gelistete Bestellung ausgestellt (`darf_korrigieren`) — das
              // Frontend kennt die Liste nicht und behauptet darum nichts über
              // diese Bestellung, nur über den Modus.
              <Alert severity="info" data-testid="modus-hinweis">
                {MODUS_HINWEIS[vorlage.modus]
                  || `The correction mode is set to “${vorlage.modus}” — issuance is restricted.`}
              </Alert>
            )}

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Items of the original invoice">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    {!schmal && <TableCell align="right">Remainder</TableCell>}
                    <TableCell align="right">Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {zeilen.map((z, i) => (
                    <TableRow key={z.shopify_line_item_id || z.position}>
                      <TableCell sx={{ fontSize: FS.sm }}>
                        {z.beschreibung || `Item ${z.position}`}
                        {z.ist_versand && <Typography component="span" sx={{ fontSize: FS.xs, color: 'text.secondary' }}> · Shipping</Typography>}
                      </TableCell>
                      {!schmal && (
                        <TableCell align="right" sx={{ fontSize: FS.sm }}>
                          {zahl(z.menge_rest)} pcs · {zahl(z.brutto_rest)} €
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <DisabledReason
                          reason={sendet
                            ? null
                            : z.menge_rest == null
                              ? 'This line cannot be corrected through the manual path.'
                              : Number(z.menge_rest) <= 0
                                ? 'This line is already fully corrected.' : null}
                        >
                          <TextField
                            size="small"
                            type="number"
                            label={`Quantity ${z.position}`}
                            inputProps={{ min: 0, max: z.menge_rest ?? undefined, step: 1 }}
                            value={mengen[i] ?? ''}
                            onChange={(e) => { setMengen((alt) => ({ ...alt, [i]: e.target.value })); formGeaendert() }}
                            error={Boolean(zeilenfehler[i] || grundNachFormIndex[i])}
                            helperText={
                              zeilenfehler[i]
                              || (grundNachFormIndex[i] || []).join(' · ')
                              // R16-W4: ein gesperrtes Feld nennt seinen Grund —
                              // „stumm disabled" liest sich wie ein Defekt.
                              || (z.menge_rest == null
                                ? 'Not correctable via the manual path'
                                : Number(z.menge_rest) <= 0
                                  ? 'Already fully corrected'
                                  : (schmal ? `Remainder ${zahl(z.menge_rest)}` : ' '))
                            }
                            disabled={sendet || z.menge_rest == null || Number(z.menge_rest) <= 0}
                            sx={{ width: 120 }}
                          />
                        </DisabledReason>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {zeilen.length === 0 && (
              // R16-W4: eine leere Tabelle plus zwei gesperrte Knöpfe ohne Grund
              // sieht wie ein Fehler aus. Der Grund steht hier.
              <Alert severity="info" data-testid="keine-positionen">
                This invoice has no correctable items — there is nothing to issue.
              </Alert>
            )}

            <Stack direction={schmal ? 'column' : 'row'} spacing={SP.card}>
              <Typography variant="body2">
                Correction amount:{' '}
                <strong data-testid="korrekturbetrag">
                  {vorschau?.korrekturbetrag != null ? `${vorschau.korrekturbetrag} €` : STRICH}
                </strong>
              </Typography>
              <Typography variant="body2">
                Refunded (target):{' '}
                <strong data-testid="erstattet">{zahl(vorlage.erstattet)} €</strong>
              </Typography>
            </Stack>
            {/* R16-W2: das Verhältnis der zwei Beträge steht VOR dem Klick — der
                422-Satz nennt es sonst erst nach dem Absenden. */}
            <Typography
              variant="caption"
              data-testid="betragsverhaeltnis"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              The correction is made against the refunded amount: the correction amount must
              {' '}total {zahl(vorlage.erstattet)} €. If it differs, nothing is issued.
            </Typography>

            <FormControl component="fieldset" disabled={sendet}>
              <FormLabel component="legend">Reason</FormLabel>
              <RadioGroup
                value={grund}
                onChange={(e) => { setGrund(e.target.value); setFehler(null); formGeaendert() }}
              >
                {GRUND_REIHENFOLGE.map((wert) => (
                  <FormControlLabel
                    key={wert}
                    value={wert}
                    control={<Radio size="small" />}
                    label={GRUND_LABEL[wert]}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {fehler && (
              <Alert severity="error">
                {fehler}
                {restGruende.length > 0 && (
                  <Box component="ul" sx={{ m: 0, mt: SP.chip, pl: SP.card }}>
                    {restGruende.map((g) => (
                      <Typography component="li" key={g} sx={{ fontSize: FS.sm }}>{g}</Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {vorschau && (
              <Box
                sx={{ border: 1, borderColor: 'divider', borderRadius: PANEL_RADIUS, p: SP.card }}
                aria-label="Preview — no document"
              >
                <Typography variant="subtitle2" sx={{ mb: SP.chip }}>
                  Preview — no document (draft DRAFT-000000)
                </Typography>
                <PdfInlineFrame url={vorschau.url} title="Correction draft"
                                frameSx={{ width: '100%', height: 420, minHeight: 240 }} />
              </Box>
            )}

            {ergebnis && (
              <Alert severity={ergebnis.zustand === 'ausgestellt' ? 'success' : 'warning'}>
                {AUSGANG_TEXT[ergebnis.zustand] || AUSGANG_TEXT.gesperrt}
                {ergebnis.nummer && ` Number ${ergebnis.nummer}.`}
                {/* 🔴 R16 O-4: der Link hängt an der NUMMER, nicht an der id.
                    `_schon_vorhanden` (`korrektur.py:1293`) setzt `invoice_id`
                    für JEDE Zeile — auch eine `cancelled`/`failed` ohne Dokument;
                    nur die `nummer` unterscheidet „ein Dokument steht" von „die
                    Zeile steht". Ohne Dokument zeigt der Link auf nichts. */}
                {ergebnis.nummer && (
                  <>
                    {' '}
                    <Box
                      component={RouterLink}
                      to={`/invoicing/${ergebnis.invoice_id}`}
                      onClick={onSchliessen}
                      sx={{ color: 'primary.main', textDecoration: 'underline' }}
                    >
                      To the document
                    </Box>
                  </>
                )}
                {/* R16-B1: die zeilenlosen Gründe des Ausgangs (z. B.
                    „Texte nicht freigegeben (P4-O1)") — ohne diesen Block stünden
                    sie nur im `fehler`-Alert, den ein 200-Ausgang nie setzt.
                    🔴 R16 W1: aus dem SCHNAPPSCHUSS (`restErgebnis`), nicht aus
                    dem lebenden `gruende`-State — „Vorschau" leert den. */}
                {restErgebnis.length > 0 && (
                  <Box component="ul" sx={{ m: 0, mt: SP.chip, pl: SP.card }}>
                    {restErgebnis.map((g) => (
                      <Typography component="li" key={g} sx={{ fontSize: FS.sm }}>{g}</Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSchliessen} disabled={sendet}>Cancel</Button>
        <DisabledReason
          reason={sendet
            ? null
            : ladegrund ? ladegrund
              : gewaehlt === 0 ? 'Select at least one line first.'
                : Object.keys(zeilenfehler).length > 0 ? 'Fix the flagged lines first.'
                  : !vorlage?.steuer_plausibel ? 'The tax case is not plausible — check the lines.' : null}
        >
          <Button onClick={holeVorschau} disabled={basisUnvollstaendig || sendet}>
            Preview
          </Button>
        </DisabledReason>
        {/* 🔴 S5.6a / R16 W2: gesperrt nur bei einem ENDGÜLTIGEN Ausgang
            (`ausgestellt`, `schon_erledigt`) — dort ersetzte ein zweiter Klick
            die angezeigte Nummer durch die idempotente Antwort und nähme dem
            Bediener den Beleg. Bei `gesperrt`/`uebersprungen` bleibt der Knopf
            aktiv: der Grund ist behebbar. Die Sperre gilt NUR für „Ausstellen";
            „Abbrechen" und der Link „Zum Dokument" schliessen weiterhin. */}
        <DisabledReason
          reason={sendet
            ? null
            : ausgangEndgueltig ? 'This correction has already been issued.'
              : ladegrund ? ladegrund
                : gewaehlt === 0 ? 'Select at least one line first.'
                  : Object.keys(zeilenfehler).length > 0 ? 'Fix the flagged lines first.'
                    : !vorlage?.steuer_plausibel ? 'The tax case is not plausible — check the lines.'
                      : !grund ? 'Enter a reason first.'
                        : !vorlage?.texte_freigegeben ? 'The wording has not been released yet.' : null}
        >
          <Button
            variant="contained"
            onClick={stelleAus}
            disabled={ausstellenGesperrt || sendet || ausgangEndgueltig}
            startIcon={sendet ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}
          >
            Issue
          </Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  )
}
